// Control lechero (resumen, ordeño del día, registros). Ganadería → Leche. Fase 2B (668).
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Milk, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import {
  Animal, Campo, LecheRegistro, LecheResumen, Lote, Modal, SortTh, TURNOS, Turno,
  fieldCls, ganRpc, hoyISO, invalidateGanaderia, num, turnoLabel, useSort,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

type SubTab = 'resumen' | 'ordeno' | 'registros';

export function Leche() {
  const [tab, setTab] = useState<SubTab>('resumen');
  const [modalOpen, setModalOpen] = useState(false);
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const admin = isAdmin();

  const tabBtn = (t: SubTab, label: string) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}
      onClick={() => setTab(t)}
    >
      {label}
    </button>
  );

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Milk className="w-5 h-5" /> Leche</h1>
      <div className="flex flex-wrap gap-1 border-b pb-2">
        {tabBtn('resumen', 'Resumen')}
        {tabBtn('ordeno', 'Ordeño del día')}
        {tabBtn('registros', 'Registros')}
      </div>

      {tab === 'resumen' && <ResumenTab />}
      {tab === 'ordeno' && <OrdenoTab admin={admin} onGuardado={() => invalidateGanaderia(qc)} />}
      {tab === 'registros' && <RegistrosTab admin={admin} onRegistrar={() => setModalOpen(true)} />}

      {modalOpen && (
        <RegistrarLecheModal onClose={() => setModalOpen(false)} onSaved={() => invalidateGanaderia(qc)} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Resumen
function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold font-mono tabular-nums mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function ResumenTab() {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const { data, isFetching } = useQuery<LecheResumen>({
    queryKey: ['gan-leche-resumen', desde, hasta],
    queryFn: () => ganRpc<LecheResumen>('gan_leche_resumen', { _desde: desde || null, _hasta: hasta || null }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const serie = useMemo(
    () => (data?.serie ?? []).map((s) => ({
      fecha: new Date(`${s.fecha}T12:00:00`).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' }),
      Litros: Number(s.litros),
    })),
    [data],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Desde" className="w-40"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta" className="w-40"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          {data && <span className="text-xs text-muted-foreground ml-auto">Ventana: {data.desde} → {data.hasta}</span>}
        </CardContent>
      </Card>

      {!data ? (
        <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Kpi label="Litros totales" value={num(data.litros_total, 0)} />
            <Kpi label="Promedio diario" value={data.promedio_diario != null ? `${num(data.promedio_diario, 1)} L` : '—'} />
            <Kpi label="Días con registro" value={String(data.dias)} />
            <Kpi label="Grasa prom." value={data.grasa_prom != null ? `${num(data.grasa_prom, 2)}%` : '—'} />
            <Kpi label="Proteína prom." value={data.proteina_prom != null ? `${num(data.proteina_prom, 2)}%` : '—'} />
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Curva de lactancia (litros/día)</CardTitle></CardHeader>
            <CardContent>
              {serie.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">Sin producción registrada en el período</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={serie} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gLitros" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="fecha" fontSize={11} />
                    <YAxis fontSize={11} width={48} />
                    <Tooltip formatter={(v: number) => `${num(v, 1)} L`} cursor={{ fill: 'currentColor', fillOpacity: 0.06 }} />
                    <Area type="monotone" dataKey="Litros" stroke="#0ea5e9" fill="url(#gLitros)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Por lote</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Lote</th><th className="py-2 pr-3 text-right">Litros</th>
                  </tr></thead>
                  <tbody>
                    {data.por_lote.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin datos</td></tr>}
                    {data.por_lote.map((l, i) => (
                      <tr key={`${l.lote}-${i}`} className="border-b last:border-0">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3">{l.lote}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(l.litros, 1)}</td>
                      </tr>
                    ))}
                    {data.por_lote.length > 0 && (
                      <tr className="border-t-2 font-semibold bg-muted/30">
                        <td className="py-2 pr-3" colSpan={2}>Totales ({data.por_lote.length})</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(data.por_lote.reduce((a, l) => a + Number(l.litros), 0), 1)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Top animales</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Arete</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3 text-right">Litros</th>
                  </tr></thead>
                  <tbody>
                    {data.top_animales.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin datos</td></tr>}
                    {data.top_animales.map((a, i) => (
                      <tr key={`${a.arete}-${i}`} className="border-b last:border-0">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3 font-medium">{a.arete}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{a.nombre ?? '—'}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(a.litros, 1)}</td>
                      </tr>
                    ))}
                    {data.top_animales.length > 0 && (
                      <tr className="border-t-2 font-semibold bg-muted/30">
                        <td className="py-2 pr-3" colSpan={3}>Totales ({data.top_animales.length})</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(data.top_animales.reduce((a, r) => a + Number(r.litros), 0), 1)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Ordeño del día
function OrdenoTab({ admin, onGuardado }: { admin: boolean; onGuardado: () => void }) {
  const [fecha, setFecha] = useState(hoyISO());
  const [turno, setTurno] = useState<Turno>('total');
  const [soloHembras, setSoloHembras] = useState(true);
  const [litrosPorAnimal, setLitrosPorAnimal] = useState<Record<string, string>>({});
  const [loteTanqueId, setLoteTanqueId] = useState('');
  const [litrosTanque, setLitrosTanque] = useState('');

  const { data: animales = [], isFetching } = useQuery<Animal[]>({
    queryKey: ['gan-animales-activos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar', { _estado: 'activo' }),
  });
  const { data: lotes = [] } = useQuery<Lote[]>({ queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar') });

  const visibles = useMemo(
    () => animales.filter((a) => !soloHembras || a.sexo === 'H'),
    [animales, soloHembras],
  );

  const guardarOrdeno = useMutation({
    mutationFn: () => {
      const registros = Object.entries(litrosPorAnimal)
        .map(([animal_id, v]) => ({ animal_id, litros: Number(v) }))
        .filter((r) => r.litros > 0);
      if (registros.length === 0) throw new Error('Ingresa los litros de al menos un animal');
      return ganRpc<{ ok: boolean; creados: number }>('gan_leche_registrar_lote', { _data: { fecha, turno, registros } });
    },
    onSuccess: (r) => {
      toast.success(`Ordeño registrado para ${r.creados} animal(es)`);
      setLitrosPorAnimal({});
      onGuardado();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardarTanque = useMutation({
    mutationFn: () => {
      if (!loteTanqueId) throw new Error('Selecciona el lote');
      const litros = Number(litrosTanque);
      if (!(litros > 0)) throw new Error('Ingresa los litros del tanque');
      return ganRpc('gan_leche_registrar', { _data: { lote_id: loteTanqueId, fecha, turno, litros } });
    },
    onSuccess: () => { toast.success('Producción de tanque registrada'); setLitrosTanque(''); onGuardado(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const conLitros = Object.values(litrosPorAnimal).filter((v) => Number(v) > 0).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Producción de tanque/lote (un solo total)</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-end">
          <Campo label="Fecha *" className="w-40"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Campo label="Turno *" className="w-40">
            <select className={fieldCls} value={turno} onChange={(e) => setTurno(e.target.value as Turno)}>
              {TURNOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Campo>
          <Campo label="Lote *" className="w-56">
            <select className={fieldCls} value={loteTanqueId} onChange={(e) => setLoteTanqueId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Litros del tanque *" className="w-40">
            <input type="number" min="0" step="0.1" className={fieldCls} value={litrosTanque} onChange={(e) => setLitrosTanque(e.target.value)} />
          </Campo>
          <Button disabled={!admin} isLoading={guardarTanque.isPending} onClick={() => guardarTanque.mutate()}>Registrar tanque</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Ordeño individual · {fecha} · {turnoLabel(turno)}</CardTitle>
          <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
            <input type="checkbox" className="h-4 w-4 rounded border-input" checked={soloHembras} onChange={(e) => setSoloHembras(e.target.checked)} />
            Solo hembras
          </label>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Arete</th><th className="py-2 pr-3">Nombre</th>
              <th className="py-2 pr-3">Lote</th><th className="py-2 pr-3 text-right w-32">Litros</th>
            </tr></thead>
            <tbody>
              {visibles.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin animales activos que mostrar</td></tr>}
              {visibles.map((a, i) => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{a.arete}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.lote_nombre ?? '—'}</td>
                  <td className="py-1 pr-3">
                    <input
                      type="number" min="0" step="0.1" className={`${fieldCls} h-8 text-right`}
                      value={litrosPorAnimal[a.id] ?? ''}
                      onChange={(e) => setLitrosPorAnimal((m) => ({ ...m, [a.id]: e.target.value }))}
                      disabled={!admin}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button disabled={!admin || conLitros === 0} isLoading={guardarOrdeno.isPending} onClick={() => guardarOrdeno.mutate()}>
          Guardar ordeño ({conLitros})
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Registros
function RegistrosTab({ admin, onRegistrar }: { admin: boolean; onRegistrar: () => void }) {
  const [animalId, setAnimalId] = useState('');
  const [loteId, setLoteId] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const { data: animales = [] } = useQuery<Animal[]>({ queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar') });
  const { data: lotes = [] } = useQuery<Lote[]>({ queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar') });
  const { data = [], isFetching } = useQuery<LecheRegistro[]>({
    queryKey: ['gan-leche-listar', animalId, loteId, desde, hasta],
    queryFn: () => ganRpc<LecheRegistro[]>('gan_leche_listar', {
      _desde: desde || null, _hasta: hasta || null, _animal: animalId || null, _lote: loteId || null,
    }),
  });

  const getVal = useCallback((r: LecheRegistro, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return r.fecha;
      case 'arete': return r.arete;
      case 'nombre': return r.nombre;
      case 'lote_nombre': return r.lote_nombre;
      case 'turno': return turnoLabel(r.turno);
      case 'litros': return Number(r.litros);
      case 'grasa_pct': return r.grasa_pct;
      case 'proteina_pct': return r.proteina_pct;
      default: return null;
    }
  }, []);
  const sort = useSort(data, getVal, 'fecha', 'desc');
  const totalLitros = data.reduce((a, r) => a + Number(r.litros), 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Animal" className="w-56">
            <select className={fieldCls} value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
              <option value="">Todos</option>
              {animales.map((a) => <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''}</option>)}
            </select>
          </Campo>
          <Campo label="Lote" className="w-48">
            <select className={fieldCls} value={loteId} onChange={(e) => setLoteId(e.target.value)}>
              <option value="">Todos</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Desde" className="w-40"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta" className="w-40"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Button className="ml-auto" disabled={!admin} onClick={onRegistrar}><Plus className="w-4 h-4 mr-1" /> Registrar individual</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Registros de ordeño ({data.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="fecha" sort={sort}>Fecha</SortTh>
              <SortTh col="turno" sort={sort}>Turno</SortTh>
              <SortTh col="arete" sort={sort}>Arete</SortTh>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="lote_nombre" sort={sort}>Lote</SortTh>
              <SortTh col="litros" sort={sort} align="right">Litros</SortTh>
              <SortTh col="grasa_pct" sort={sort} align="right">Grasa %</SortTh>
              <SortTh col="proteina_pct" sort={sort} align="right">Proteína %</SortTh>
              <th className="py-2 pr-3">Observaciones</th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin registros de ordeño</td></tr>}
              {sort.sorted.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{r.fecha}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{turnoLabel(r.turno)}</td>
                  <td className="py-2 pr-3 font-medium">{r.arete ?? '—'}</td>
                  <td className="py-2 pr-3">{r.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.lote_nombre ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(r.litros, 1)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.grasa_pct != null ? num(r.grasa_pct, 2) : '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.proteina_pct != null ? num(r.proteina_pct, 2) : '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.observaciones ?? '—'}</td>
                </tr>
              ))}
              {sort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={6}>Totales ({sort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(totalLitros, 1)}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// -------------------------------------------------- Registrar individual ----
function RegistrarLecheModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [destino, setDestino] = useState<'animal' | 'lote'>('animal');
  const [animalId, setAnimalId] = useState('');
  const [loteId, setLoteId] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [turno, setTurno] = useState<Turno>('total');
  const [litros, setLitros] = useState('');
  const [grasa, setGrasa] = useState('');
  const [proteina, setProteina] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const { data: animales = [] } = useQuery<Animal[]>({ queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar') });
  const { data: lotes = [] } = useQuery<Lote[]>({ queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar') });

  const registrar = useMutation({
    mutationFn: () => ganRpc('gan_leche_registrar', {
      _data: {
        animal_id: destino === 'animal' ? (animalId || null) : null,
        lote_id: destino === 'lote' ? (loteId || null) : null,
        fecha, turno, litros: Number(litros),
        grasa_pct: grasa === '' ? null : Number(grasa),
        proteina_pct: proteina === '' ? null : Number(proteina),
        observaciones: observaciones || null,
      },
    }),
    onSuccess: () => { toast.success('Registro de leche guardado'); onSaved(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = Number(litros) > 0 && (destino === 'animal' ? !!animalId : !!loteId);

  return (
    <Modal title="Registrar producción de leche" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Destino *">
            <select className={fieldCls} value={destino} onChange={(e) => setDestino(e.target.value as 'animal' | 'lote')}>
              <option value="animal">Animal individual</option>
              <option value="lote">Lote / tanque</option>
            </select>
          </Campo>
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
        </div>
        {destino === 'animal' ? (
          <Campo label="Animal *">
            <select className={fieldCls} value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {animales.map((a) => <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''}</option>)}
            </select>
          </Campo>
        ) : (
          <Campo label="Lote *">
            <select className={fieldCls} value={loteId} onChange={(e) => setLoteId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Turno *">
            <select className={fieldCls} value={turno} onChange={(e) => setTurno(e.target.value as Turno)}>
              {TURNOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Campo>
          <Campo label="Litros *"><input type="number" min="0" step="0.1" className={fieldCls} value={litros} onChange={(e) => setLitros(e.target.value)} /></Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Grasa %"><input type="number" min="0" step="0.01" className={fieldCls} value={grasa} onChange={(e) => setGrasa(e.target.value)} /></Campo>
          <Campo label="Proteína %"><input type="number" min="0" step="0.01" className={fieldCls} value={proteina} onChange={(e) => setProteina(e.target.value)} /></Campo>
        </div>
        <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={registrar.isPending} disabled={!puedeGuardar} onClick={() => registrar.mutate()}>Registrar</Button>
        </div>
      </div>
    </Modal>
  );
}
